<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { SidebarHeader, toast } from 'frappe-ui'
import { session, switchCompany } from '../session'
import {
  workspace,
  workspaceBranches,
  workspaceHasChoice,
  setWorkspaceBranch,
} from '../workspace'
import { errorMessage } from '../api'
import BrandLogo from './BrandLogo.vue'
import { allowUnloadOnce, confirmDiscard } from '../composables/useDirtyGuard'

const router = useRouter()

const branches = computed(() => workspaceBranches())

const activeSalonName = computed(() => session.boot?.company || 'Glamora')

const subtitle = computed(() => {
  if (workspace.branch === '') return 'All branches'
  const branch = branches.value.find((b) => b.name === workspace.branch)
  return branch?.label || workspace.branch
})

/** Menu items for the active salon's branches (including "All branches"). */
const branchMenuItems = computed(() => {
  return branches.value.map((b) => ({
    label: b.label,
    icon: b.name === workspace.branch ? 'lucide-check' : undefined,
    onClick: async () => {
      const ok = await setWorkspaceBranch(b.name)
      if (!ok) return
    },
  }))
})

/** Menu items for other salon roots (switchCompany then set branch). */
const otherSalonMenuItems = computed(() => {
  const boot = session.boot
  if (!boot?.companies?.length) return []

  const activeRoot = boot.company
  const otherRoots = boot.companies.filter((c) => c !== activeRoot)

  return otherRoots.map((root) => ({
    group: root,
    options: [
      {
        label: 'All branches',
        icon: undefined,
        onClick: async () => {
          const ok = await confirmDiscard()
          if (!ok) return
          // The reload below IS the discard the user just approved: do not let
          // the unload guard ask the same question twice.
          allowUnloadOnce()
          try {
            await switchCompany(root)
            router.go(0)
          } catch (e) {
            toast.error(`Could not switch to ${root}: ${errorMessage(e)}`)
          }
        },
      },
    ],
  }))
})

const allMenuItems = computed(() => {
  const items = [
    ...(branches.value.length > 1 ? branchMenuItems.value : []),
    ...otherSalonMenuItems.value,
  ]
  return items
}) as any
</script>

<template>
  <SidebarHeader
    v-if="workspaceHasChoice"
    :title="activeSalonName"
    :subtitle="subtitle"
    :menuItems="allMenuItems"
    :showLogo="false"
    class="min-w-0 flex-1"
  >
    <template #logo>
      <BrandLogo :size="24" />
    </template>
  </SidebarHeader>

  <div v-else class="flex min-w-0 flex-1 items-center gap-2 px-1">
    <BrandLogo :size="24" />
    <div class="min-w-0 flex-1">
      <div class="truncate text-sm font-medium text-ink-gray-9">Glamora</div>
      <div class="truncate text-xs text-ink-gray-5">
        {{ activeSalonName }}
      </div>
    </div>
  </div>
</template>
